import React from 'react';
import { Filter, RotateCcw, Search } from 'lucide-react';
import { CustomDropdown } from '../../../../components/Common/CustomDropdown';

export interface ChannelFilterState {
  distributorType: string;
  searchQuery: string;
  status: string;
}

interface DistributorChannelFiltersProps {
  filters: ChannelFilterState;
  onFilterChange: (filters: ChannelFilterState) => void;
  onReset: () => void;
}

export const DistributorChannelFilters: React.FC<DistributorChannelFiltersProps> = ({
  filters,
  onFilterChange,
  onReset,
}) => {
  const handleChange = (field: keyof ChannelFilterState, value: string) => {
    onFilterChange({
      ...filters,
      [field]: value,
    });
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-[#ECECF2] shadow-sm mb-6 select-none">
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-12 gap-4 items-end">
        {/* Distributor Type Dropdown */}
        <div className="lg:col-span-4">
          <CustomDropdown
            label="Distributor Type"
            placeholder="Select Distributor Type"
            options={['Select Distributor Type', 'GT (General Trade)', 'MT (Modern Trade)']}
            value={filters.distributorType === 'GT' ? 'GT (General Trade)' : filters.distributorType === 'MT' ? 'MT (Modern Trade)' : 'Select Distributor Type'}
            onChange={(val: string) => {
              const clean = val.startsWith('GT') ? 'GT' : val.startsWith('MT') ? 'MT' : '';
              handleChange('distributorType', clean);
            }}
            searchable={false}
          />
        </div>

        {/* Search Distributor Input */}
        <div className="lg:col-span-4 flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">Search Distributor</label>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by code or name"
              value={filters.searchQuery}
              onChange={(e) => handleChange('searchQuery', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3.5 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
            />
          </div>
        </div>

        {/* Status Dropdown */}
        <div className="lg:col-span-2">
          <CustomDropdown
            label="Status"
            placeholder="All Status"
            options={['All Status', 'Active', 'Inactive']}
            value={filters.status || 'All Status'}
            onChange={(val: string) => handleChange('status', val === 'All Status' ? '' : val)}
            searchable={false}
          />
        </div>

        {/* Buttons */}
        <div className="lg:col-span-2 flex items-center justify-end gap-2.5">
          <button
            onClick={onReset}
            className="flex-1 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs"
          >
            <RotateCcw className="w-3.5 h-3.5 text-slate-400" />
            <span>Reset</span>
          </button>

          <button
            className="flex-1 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-[#C1272D]/20 active:scale-95"
          >
            <Filter className="w-3.5 h-3.5" />
            <span>Filter</span>
          </button>
        </div>
      </div>
    </div>
  );
};
